/**
 * Overview tab — review loops, BDD scenarios, performance, learnings.
 */

import { esc } from '../../../shared/rendering.js'
import { renderSection } from '../../components/section.js'
import { renderReviewLoop } from '../../components/reviewLoop.js'
import { renderBDDScenarios } from '../../components/bddScenarios.js'
import { renderPerfStrip } from '../../components/perfStrip.js'
import { renderLearnings } from '../../components/learnings.js'
import { formatDuration } from '../../../types/domain.js'
import type { RunOverview } from '../../../types/api.js'

/** Render the Overview tab content. */
export function renderOverviewTab(data: RunOverview): string {
  const parts: string[] = [renderEvidenceOverview(data)]

  // Review Loops
  if (data.loops.length > 0) {
    const rejected = data.loops.filter((l) => l.status === 'rejected').length
    const loopsHtml = data.loops
      .map((loop) =>
        renderReviewLoop({
          loopNumber: loop.loop_number,
          status: loop.status,
          gates: loop.gates.map((g) => ({
            type: g.approved ? 'pass' : 'fail',
            label: `${g.reviewer} ${g.approved ? 'Passed' : 'Rejected'}`,
            summary: g.summary ?? undefined,
            detail: g.feedback ?? undefined,
            response: g.response ?? undefined,
            files: [...g.files],
          })),
          consolidate: loop.consolidate_feedback
            ? {
                feedback: loop.consolidate_feedback,
                response: loop.consolidate_response ?? undefined,
              }
            : undefined,
          defaultOpen: false,
        }),
      )
      .join('')

    parts.push(
      renderSection({
        title: 'Review Loops',
        badge: `${data.loops.length} loops \u00b7 ${rejected} rejected`,
        content: loopsHtml,
        defaultOpen: false,
      }),
    )
  }

  // BDD Scenarios
  if (data.bdd_scenarios && data.bdd_scenarios.length > 0) {
    const bddHtml = renderBDDScenarios({ scenarios: data.bdd_scenarios })
    parts.push(
      renderSection({
        title: 'BDD Scenarios',
        badge: `${data.bdd_scenarios.length}`,
        content: bddHtml,
        defaultOpen: false,
      }),
    )
  }

  // Performance
  if (data.latency_contributors.length > 0) {
    // Contributors whose label contains "LLM" represent pure LLM time
    // (covers main agent + any sub-agent LLM time).
    const LLM_CONTRIBUTOR_PATTERN = 'LLM'
    const totalLLMS = data.latency_contributors
      .filter((c) => c.label.includes(LLM_CONTRIBUTOR_PATTERN))
      .reduce((sum, c) => sum + c.total_duration_s, 0)
    const totalLLMLatency =
      totalLLMS > 0 ? (formatDuration(totalLLMS) ?? `${totalLLMS.toFixed(1)}s`) : undefined

    const perfHtml = renderPerfStrip({
      contributors: data.latency_contributors.map((c) => ({
        label: c.label,
        avgDuration: formatDuration(c.avg_duration_s) ?? '0s',
        totalCalls: c.total_calls,
        totalDuration: formatDuration(c.total_duration_s) ?? '0s',
        percent: Math.round(c.percent_of_total),
      })),
      toolSummary: {
        totalCalls: data.tool_summary.total_calls,
        totalErrors: data.tool_summary.total_errors,
        totalLLMCalls: data.tool_summary.total_llm_calls,
        totalLLMLatency,
      },
      // TODO(future): backend should provide model_latency_breakdown in RunOverview
      // modelContributors: data.model_latency_breakdown?.map(m => ({ ... })),
    })
    parts.push(
      renderSection({
        title: 'Performance',
        content: perfHtml,
        defaultOpen: true,
      }),
    )
  }

  // Learnings
  if (data.learnings.length > 0) {
    const learningsHtml = renderLearnings(
      data.learnings.map((l) => ({
        type: l.type,
        title: l.title,
        body: l.body,
      })),
    )
    parts.push(
      renderSection({
        title: 'Learnings',
        badge: `${data.learnings.length} entries`,
        content: learningsHtml,
        defaultOpen: true,
      }),
    )
  }

  return parts.join('')
}

function renderEvidenceOverview(data: RunOverview): string {
  const s = data.summary
  const verdict = summarizeVerdict(s.status)
  const duration = formatDuration(s.duration_s) ?? 'Live'
  const stageSummary = summarizeStages(data)
  const cards: [string, string, string][] = [
    ['Verdict', verdict.label, verdict.tone],
    ['Events', `${s.total_events}`, 'cyn'],
  ]
  if (s.total_turns > 0) cards.push(['Turns', String(s.total_turns), 'pur'])
  cards.push(
    ['Tools', `${data.tool_summary.total_calls}`, 'grn'],
    ['Assistant calls', `${data.tool_summary.total_llm_calls}`, 'pnk'],
    ['Duration', duration, 'wrn'],
  )
  const cardHtml = cards
    .map(
      ([label, value, tone]) => `<div class="ov-card ${esc(tone)}">
        <span class="ov-card-k">${esc(label)}</span>
        <span class="ov-card-v">${esc(value)}</span>
      </div>`,
    )
    .join('')

  const evidenceRowPairs: [string, string][] = [
    ['Current story', s.task_title ?? s.work_item_id],
    ['Execution path', stageSummary],
    ['Evidence deck', `${s.total_events} trace events ready for inspection`],
  ]
  const evidenceRows = evidenceRowPairs
    .map(
      ([label, value]) =>
        `<div class="si"><span class="si-txt" style="color:var(--t3)">${esc(label)}</span><span style="margin-left:auto;text-align:right;color:var(--t1);font-size:12px">${esc(value)}</span></div>`,
    )
    .join('')

  return `<div class="ov-hero">
    <div class="ov-hero-head">
      <div>
        <div class="sl">Evidence Summary</div>
        <div class="ov-verdict ${esc(verdict.tone)}">${esc(verdict.copy)}</div>
      </div>
      <div class="ov-actions">
        <button class="fp on" type="button" data-run-tab="deck">View Evidence</button>
        <button class="fp" type="button" data-run-tab="timeline">Open Timeline</button>
      </div>
    </div>
    <div class="ov-grid">${cardHtml}</div>
    ${renderSection({ title: 'Run evidence preview', badge: s.status, content: evidenceRows, defaultOpen: true })}
  </div>`
}

function summarizeVerdict(status: string): { label: string; copy: string; tone: string } {
  if (status === 'completed') {
    return {
      label: 'Evidence ready',
      copy: 'Completed — review the evidence deck before approving.',
      tone: 'grn',
    }
  }
  if (status === 'running') {
    return { label: 'Live', copy: 'Running — trace events are streaming now.', tone: 'cyn' }
  }
  if (status === 'failed') {
    return {
      label: 'Action required',
      copy: 'Failed — inspect errors and rerun after correction.',
      tone: 'err',
    }
  }
  return { label: status, copy: status, tone: 'cyn' }
}

function summarizeStages(data: RunOverview): string {
  if (data.execution_graph.length === 0) return 'No execution graph captured yet'
  return data.execution_graph
    .slice(0, 4)
    .map((node) => `${node.stage}${node.iteration ? ` #${node.iteration}` : ''}`)
    .join(' → ')
}
